import React from 'react';
import { CzechSuklRsAtcGroupResponse } from 'com.cogvio.pricemonitor.api';
import { Checkbox } from '~/libs/ui/atoms/Checkbox';
import { TailwindTooltip } from '~/libs/ui/atoms/TailwindTooltip';

import { AVAILABILITY_COLUMN } from '../constants';
import { getAvailabilityTooltip } from '../utils/availabilityTooltip';
import { availabilityGroupExpandKey } from '../utils/availabilityRowExpandKey';
import { iterateGroupProducts } from '../utils/iterateGroupProducts';
import { resolveAvailabilityMetric } from '../utils/resolveAvailabilityMetric';
import { formatIdenticalLpCount } from '../utils/formatIdenticalLpCount';
import { formatProductDncDou } from '../utils/formatVersionDncDou';
import { AvailabilityShareBar } from './AvailabilityShareBar';
import { RowAvailabilityIcon } from './RowAvailabilityIcon';
import { RowCard } from './RowCard';
import { TruncatedText } from './TruncatedText';

export type RowLayout = 'grid' | 'stacked';

export interface RowProps {
	group: CzechSuklRsAtcGroupResponse;
	dddShareThreshold: number;
	layout?: RowLayout;
	startRow?: number;
	showGroupSeparator?: boolean;
	selectable?: boolean;
	selected?: boolean;
	selectionDisabled?: boolean;
	/** Hides the checkbox control while keeping selection working via clicking the row. */
	hideCheckbox?: boolean;
	onToggleSelect?(group: CzechSuklRsAtcGroupResponse, selected: boolean): void;
	expandedKeys?: Set<string>;
	onToggleExpand?(key: string): void;
}

function multiVersionPackBorder(versionIndex: number, versionCount: number): string {
	if (versionCount <= 1) {
		return '';
	}

	return [
		'xl:border-dashed xl:border-gray-300 xl:border-l xl:border-t',
		versionIndex === versionCount - 1 ? 'xl:border-b' : '',
	].join(' ');
}

function multiVersionSuklBorder(versionIndex: number, versionCount: number): string {
	if (versionCount <= 1) {
		return '';
	}

	return [
		'xl:border-dashed xl:border-gray-300 xl:border-r xl:border-t',
		versionIndex === versionCount - 1 ? 'xl:border-b' : '',
	].join(' ');
}

export function Row({
	group,
	dddShareThreshold,
	layout = 'stacked',
	startRow = 1,
	showGroupSeparator,
	selectable,
	selected,
	selectionDisabled,
	hideCheckbox,
	onToggleSelect,
	expandedKeys,
	onToggleExpand,
}: RowProps) {
	const isGrid = layout === 'grid';

	const cardProps = {
		group,
		dddShareThreshold,
		selectable,
		selected,
		selectionDisabled,
		hideCheckbox,
		onToggleSelect,
	};

	if (!isGrid) {
		return <RowCard {...cardProps} />;
	}

	const products = iterateGroupProducts(group);
	const rowSpan = Math.max(
		products.reduce((sum, { versions }) => sum + Math.max(versions.length, 1), 0),
		1
	);
	const isExpandable = Boolean(onToggleExpand);
	const groupExpandKey = availabilityGroupExpandKey(group.id);
	const isGroupExpanded = expandedKeys?.has(groupExpandKey) ?? false;

	const [atcCode, ...atcRest] = group.atc.split(' ');
	const activeSubstance = atcRest.join(' ');

	const position = (column: number, row: number, span = 1): React.CSSProperties => ({
		gridColumn: column,
		gridRow: span > 1 ? `${row} / span ${span}` : row,
	});

	const handleGroupToggle = () => {
		onToggleExpand?.(groupExpandKey);
	};

	const stopPropagation: React.MouseEventHandler = (event) => {
		event.stopPropagation();
	};

	const selectionCell = selected ? ' xl:bg-cBlue-50' : ' xl:group-hover:bg-gray-50';
	const groupSeparatorCell = showGroupSeparator ? ' xl:pt-4 xl:border-t xl:border-gray-300' : '';
	const expandInteractionClass = isExpandable ? ' xl:cursor-pointer' : '';

	const cellClass = (decoration: string, isLastColumn = false) =>
		` xl:w-auto xl:pb-3${isLastColumn ? '' : ' xl:pr-6'}${decoration}`;

	const sectionCellClass = (decoration: string, isLastColumn = false) =>
		` xl:w-auto${isLastColumn ? '' : ' xl:pr-6'}${decoration}`;

	const sectionPadding = ' xl:px-3 xl:py-4';
	const spanningSectionSurface = isGroupExpanded ? ' xl:bg-gray-50' : ' xl:bg-transparent';
	const spanningSectionDecoration = `${groupSeparatorCell}${selectionCell}${sectionPadding}${spanningSectionSurface}${expandInteractionClass}`;

	const spanningCellProps = isExpandable
		? {
				role: 'button' as const,
				tabIndex: 0,
				'aria-expanded': isGroupExpanded,
				onClick: handleGroupToggle,
				onKeyDown: (event: React.KeyboardEvent) => {
					if (event.key === 'Enter' || event.key === ' ') {
						event.preventDefault();
						handleGroupToggle();
					}
				},
		  }
		: {};

	let gridRow = startRow;
	const productRows: React.ReactNode[] = [];

	products.forEach(({ product, productIndex, versions }) => {
		const metric = resolveAvailabilityMetric(product.availability);
		const mainBrand = versions[0]?.brand ?? '';
		const versionCount = Math.max(versions.length, 1);
		const isMultiVersion = versions.length > 1;
		const dncDouDisplay = formatProductDncDou(versions);
		const isFirstProduct = productIndex === 0;
		const productStartRow = gridRow;
		const productSeparator = isFirstProduct ? groupSeparatorCell : ' xl:pt-3 xl:border-t xl:border-gray-300';
		const productSurface = isGroupExpanded ? ' xl:bg-gray-50' : ' xl:bg-transparent';
		const productDecoration = `${productSeparator}${selectionCell}${sectionPadding}${productSurface}${expandInteractionClass}`;
		const rowCellProps = spanningCellProps;

		if (versions.length === 0) {
			productRows.push(
				<React.Fragment key={`product-${productIndex}`}>
					<div
						className={`xl:min-w-0${sectionCellClass(`${productDecoration} xl:pb-3`)}`}
						style={position(AVAILABILITY_COLUMN.brand, productStartRow)}
						{...rowCellProps}
					>
						<span className='text-gray-400'>–</span>
					</div>
					<div
						className={`xl:min-w-0${sectionCellClass(`${productDecoration} xl:pb-3`)}`}
						style={position(AVAILABILITY_COLUMN.pack, productStartRow)}
					/>
					<div
						className={`xl:text-right${sectionCellClass(`${productDecoration} xl:pb-3`)}`}
						style={position(AVAILABILITY_COLUMN.suklCode, productStartRow)}
					/>
					<div
						className={sectionCellClass(`${productDecoration} xl:pb-3`)}
						style={position(AVAILABILITY_COLUMN.share, productStartRow)}
					/>
					<div
						className={`xl:flex xl:flex-col xl:justify-end${sectionCellClass(`${productDecoration} xl:pb-3`)}`}
						style={position(AVAILABILITY_COLUMN.dncDou, productStartRow)}
					/>
					<div
						className={`xl:flex xl:flex-col xl:justify-end${sectionCellClass(`${productDecoration} xl:pb-3`, true)}`}
						style={position(AVAILABILITY_COLUMN.status, productStartRow)}
					/>
				</React.Fragment>
			);
			gridRow += 1;
			return;
		}

		productRows.push(
			<React.Fragment key={`product-${productIndex}`}>
				<div
					className={`xl:min-w-0 xl:flex xl:flex-col xl:justify-start${sectionCellClass(productDecoration)}`}
					style={position(AVAILABILITY_COLUMN.brand, productStartRow, versionCount)}
					{...rowCellProps}
				>
					{isMultiVersion ? (
						<div className='flex items-stretch gap-2 min-h-full'>
							<div className='w-0.5 flex-shrink-0 bg-cBlue-400 self-stretch' aria-hidden='true' />
							<div className='min-w-0 py-0.5'>
								<TruncatedText
									text={mainBrand}
									className='text-base font-bold text-gray-900 leading-5 uppercase'
									expanded={isGroupExpanded}
								/>
								<span className='block mt-1 text-xs font-semibold text-cBlue-600 uppercase tracking-wide'>
									{formatIdenticalLpCount(versions.length)}
								</span>
							</div>
						</div>
					) : (
						<TruncatedText
							text={mainBrand}
							className='text-base font-bold text-gray-900 leading-5 uppercase'
							expanded={isGroupExpanded}
						/>
					)}
				</div>

				<div
					className={sectionCellClass(productDecoration)}
					style={position(AVAILABILITY_COLUMN.share, productStartRow, versionCount)}
					{...rowCellProps}
				>
					<AvailabilityShareBar
						label={metric.label}
						value={metric.value}
						share={metric.share}
						shareThreshold={dddShareThreshold}
					/>
				</div>

				<div
					className={`xl:flex xl:flex-col xl:justify-start xl:pt-1 xl:text-center${sectionCellClass(
						productDecoration
					)}`}
					style={position(AVAILABILITY_COLUMN.dncDou, productStartRow, versionCount)}
					{...rowCellProps}
				>
					<div className='text-sm leading-6 text-gray-900 uppercase w-full'>
						<span>{dncDouDisplay}</span>
					</div>
				</div>

				<div
					className={`xl:flex xl:flex-col xl:justify-start${sectionCellClass(productDecoration, true)}`}
					style={position(AVAILABILITY_COLUMN.status, productStartRow, versionCount)}
					{...rowCellProps}
				>
					<div className='xl:flex xl:justify-center xl:pt-1' onClick={stopPropagation}>
						<TailwindTooltip
							reference={<RowAvailabilityIcon status={product.availability.status} />}
							displayClass='relative inline-block'
							position='left'
						>
							{getAvailabilityTooltip({
								status: product.availability.status,
								isOverThreshold: product.availability.decisionDetails.isOverThreshold,
								isFullyReimbursed: product.availability.decisionDetails.isFullyReimbursed,
								hasPriceAgreement: product.availability.decisionDetails.hasPriceAgreement,
								thresholdPercent: dddShareThreshold,
							})}
						</TailwindTooltip>
					</div>
				</div>

				{versions.map((version, versionIndex) => {
					const versionRow = productStartRow + versionIndex;
					const isSubsequentVersion = versionIndex > 0;
					const versionBrand = version.brand ?? '';
					const showSecondaryBrand = isSubsequentVersion && versionBrand && versionBrand !== mainBrand;
					const versionDecoration = isMultiVersion
						? `${selectionCell}${sectionPadding}${productSurface}${expandInteractionClass} xl:py-3`
						: `${selectionCell}${sectionPadding}${productSurface}${expandInteractionClass} xl:pb-3`;
					const packBorder = multiVersionPackBorder(versionIndex, versionCount);
					const suklBorder = multiVersionSuklBorder(versionIndex, versionCount);
					const isFadedVersion = isMultiVersion && isSubsequentVersion;
					const packDescriptionClass =
						isMultiVersion && !isSubsequentVersion
							? 'text-md font-bold text-gray-900 leading-5 uppercase tabular-nums'
							: 'text-md font-semibold text-gray-900 leading-5 uppercase tabular-nums';
					const suklClass =
						isMultiVersion && !isSubsequentVersion
							? 'text-label font-bold text-gray-900 leading-5 uppercase tabular-nums'
							: 'text-label font-semibold text-gray-900 leading-5 uppercase tabular-nums';
					const fadedCellClass = isFadedVersion ? ' xl:opacity-60' : '';

					return (
						<React.Fragment key={version.suklCode ?? `${productIndex}-${versionIndex}`}>
							<div
								className={`xl:min-w-0${sectionCellClass(`${versionDecoration} ${packBorder}`)}${fadedCellClass}`}
								style={position(AVAILABILITY_COLUMN.pack, versionRow)}
								{...rowCellProps}
							>
								{isSubsequentVersion ? (
									<span className='block mb-0.5 text-xs text-gray-400 uppercase tabular-nums'>Totožný LP</span>
								) : null}
								{showSecondaryBrand ? (
									<TruncatedText
										text={versionBrand}
										className='block mb-0.5 text-base font-bold text-gray-900 leading-5 uppercase'
										expanded={isGroupExpanded}
									/>
								) : null}
								<TruncatedText
									text={version.description ?? ''}
									className={packDescriptionClass}
									expanded={isGroupExpanded}
								/>
							</div>
							<div
								className={`xl:text-right${sectionCellClass(`${versionDecoration} ${suklBorder}`)}${fadedCellClass}`}
								style={position(AVAILABILITY_COLUMN.suklCode, versionRow)}
								{...rowCellProps}
							>
								<h2 className={suklClass}>{version.suklCode ?? '–'}</h2>
							</div>
						</React.Fragment>
					);
				})}
			</React.Fragment>
		);

		gridRow += versionCount;
	});

	return (
		<>
			<RowCard className='xl:hidden mb-3' {...cardProps} />
			<div className='hidden xl:contents font-display group'>
				{selectable && (
					<div
						className={`xl:pt-0.5${cellClass(`${groupSeparatorCell}${selectionCell}`)}`}
						style={position(AVAILABILITY_COLUMN.selection, startRow, rowSpan)}
						onClick={stopPropagation}
					>
						<Checkbox
							id={`availability-row-select-${group.id}`}
							checked={Boolean(selected)}
							disabled={selectionDisabled && !selected}
							onChange={(checked) => onToggleSelect?.(group, checked)}
						/>
					</div>
				)}
				<div
					className={sectionCellClass(spanningSectionDecoration)}
					style={position(AVAILABILITY_COLUMN.rs, startRow, rowSpan)}
					{...spanningCellProps}
				>
					<h2 className='text-md font-semibold text-gray-900 leading-5 uppercase tabular-nums'>{group.rs ?? '–'}</h2>
				</div>
				<div
					className={`xl:min-w-0${sectionCellClass(spanningSectionDecoration)}`}
					style={position(AVAILABILITY_COLUMN.atc, startRow, rowSpan)}
					{...spanningCellProps}
				>
					<h2 className='xl:flex xl:min-w-0 text-base font-bold text-gray-900 leading-5 uppercase'>
						<span className='xl:flex-shrink-0 text-cBlue-500 mr-2 tabular-nums'>{atcCode}</span>
						<TruncatedText text={activeSubstance} className='xl:flex-1 xl:min-w-0' expanded={isGroupExpanded} />
					</h2>
				</div>
				{productRows}
			</div>
		</>
	);
}
